<template>
  <div
    v-if="tokenUser"
    class="em-page"
    :class="{ 'em-page--client': !isAdmin }"
  >
    <div class="container">
      <div class="section">
        <div class="em-stack">
          <div class="em-head">
            <div>
              <h1 class="em-title">Empresas</h1>
              <p class="em-subtitle">
                Consulta las empresas, sus diagnósticos y entra a trabajar en
                una de ellas
              </p>
            </div>
            <div class="em-current">
              <span class="em-current-label">Empresa seleccionada</span>
              <span class="em-current-name">{{ selectedName }}</span>
            </div>
          </div>

          <div v-if="isAdmin" class="em-indicators">
            <div class="em-summary">
              <span>
                <b>{{ formatNumber(header.companies) }}</b>
                empresas
                {{
                  year ? "(con datos en " + year + ")" : "(en todos los años)"
                }}
              </span>
              <span>
                <b>{{ formatNumber(header.processTotal) }}</b>
                diagnósticos
              </span>
            </div>
            <div class="em-cards">
              <button
                v-for="mod in modules"
                :key="mod.key"
                v-tooltip="cardTooltip(mod)"
                type="button"
                class="em-card"
                :class="{ 'is-active': moduleFilter === mod.key }"
                :aria-pressed="moduleFilter === mod.key ? 'true' : 'false'"
                @click="toggleModule(mod.key)"
              >
                <span class="em-card-icon">
                  <i class="material-icons">{{ mod.icon }}</i>
                </span>
                <span class="em-card-text">
                  <span class="em-card-value">{{
                    formatNumber(header.modules[mod.key])
                  }}</span>
                  <span class="em-card-label">{{ mod.label }}</span>
                </span>
              </button>
            </div>
          </div>

          <div class="em-filters">
            <div class="em-search">
              <i class="material-icons">search</i>
              <input
                v-model="searchTerm"
                type="search"
                name="search"
                placeholder="Buscar por nombre o NIT..."
                maxlength="100"
                autocomplete="off"
                aria-label="Buscar empresas"
                @input="onSearchInput"
              />
            </div>
            <div class="em-spacer"></div>
            <year-picker
              v-if="isAdmin"
              :year="year"
              :range-dates="rangeDates"
              @change="onYearChange"
            ></year-picker>
            <button
              v-if="isAdmin"
              type="button"
              class="em-btn em-btn--dark"
              @click="openCreate"
            >
              <i class="material-icons">add</i>
              <span>Crear empresa</span>
            </button>
          </div>

          <div v-if="hasFilters" class="em-chips">
            <template v-if="hasFilters">
              <span>Filtros:</span>
              <button
                v-if="appliedSearch"
                type="button"
                class="em-chip"
                @click="removeSearchChip"
              >
                Búsqueda: «{{ appliedSearch }}»
              </button>
              <button
                v-if="moduleFilter"
                type="button"
                class="em-chip"
                @click="removeModuleChip"
              >
                Módulo: {{ moduleFilterLabel }}
              </button>
              <button type="button" class="em-linkbtn" @click="clearFilters">
                Quitar todos
              </button>
            </template>
          </div>

          <div class="em-box">
            <ag-grid-vue
              v-show="showGrid"
              class="ag-theme-material em-grid"
              :class="{ 'em-grid--compact': isCompact }"
              :gridOptions="gridOptions"
              :columnDefs="columnDefs"
              :defaultColDef="defaultColDef"
              @grid-ready="onGridReady"
              @sort-changed="onSortChanged"
            >
            </ag-grid-vue>

            <div v-if="phase === 'empty'">
              <div v-if="hasAnyFilter" class="em-state">
                <strong>
                  No se encontraron empresas para: {{ filtersDescription }}
                </strong>
                <button
                  v-if="hasFilters"
                  type="button"
                  class="em-btn em-btn--dark"
                  @click="clearFilters"
                >
                  Quitar todos
                </button>
              </div>
              <div v-else-if="!pinnedRow" class="em-state">
                <strong>No hay empresas</strong>
              </div>
            </div>

            <div v-if="phase === 'error'" class="em-state">
              <strong>No se pudo cargar la lista de empresas</strong>
              <button
                type="button"
                class="em-btn em-btn--dark"
                @click="reload()"
              >
                Reintentar
              </button>
            </div>

            <div v-if="phase === 'forbidden'" class="em-state">
              <strong>Tu cuenta no tiene permiso para ver empresas</strong>
            </div>

            <div v-if="showGrid && blockError" class="em-strip">
              <span>No se pudo cargar un bloque de empresas</span>
              <button
                type="button"
                class="em-btn em-btn--dark em-btn--small"
                @click="retryBlock"
              >
                Reintentar
              </button>
            </div>

            <div v-show="showGrid" class="em-footer">
              <span class="em-footer-state">{{ footerState }}</span>
              <span v-if="total === null">Cargando empresas...</span>
              <span v-else>
                Mostrando {{ formatNumber(loadedCount) }} de
                {{ formatNumber(total) }} empresas
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <transition name="em-slide">
      <side-menu
        v-if="showSideMenu"
        :updateData="sideMenuData.updateData"
        :dataValidate="validateList"
        @switchSideMenu="closeSideMenu"
        @getCompanies="onCompaniesChanged"
      ></side-menu>
    </transition>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { AgGridVue } from "ag-grid-vue";
import CompanyActions from "../components/CompanyActions.vue";
import DiagnosisTooltip from "../components/DiagnosisTooltip.vue";
import SideMenu from "../components/SideMenu.vue";
import YearPicker from "../components/YearPicker.vue";
import {
  deleteCompanyRequest,
  fetchCompaniesBlock,
  fetchCompaniesHeader,
  selectCompanyRequest,
} from "../api";
import { ADMIN_COLUMNS, CLIENT_COLUMNS } from "../columns";
import {
  EMPTY_HEADER,
  MODULES,
  buildCompaniesParams,
  describeFilters,
  escapeHtml,
  formatNumber,
  getLastProcess,
  getModule,
  isValidCompanyId,
  normalizeSearch,
  resolveDiagnosisRoute,
} from "../helpers";

const BLOCK_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 400;
const EDIT_DELAY_MS = 200;
const NOTICE_MS = 1700;

export default {
  name: "CompaniesList",
  components: {
    AgGridVue,
    SideMenu,
    YearPicker,
    /* eslint-disable-next-line vue/no-unused-components */
    companyActions: CompanyActions,
    /* eslint-disable-next-line vue/no-unused-components */
    diagnosisTooltip: DiagnosisTooltip,
  },
  data() {
    return {
      searchTerm: "",
      appliedSearch: "",
      moduleFilter: null,
      year: null,
      phase: "idle",
      total: null,
      loadedBlocks: {},
      blockError: null,
      pinnedRow: null,
      header: EMPTY_HEADER,
      rangeDates: null,
      validateList: [],
      selectingIds: [],
      deletingIds: [],
      showSideMenu: false,
      sideMenuData: {
        updateData: {},
      },
      gridApi: null,
      generation: 0,
      headerGeneration: 0,
      searchTimer: null,
      editTimer: null,
      gridOptions: {
        context: {
          componentParent: this,
        },
        rowModelType: "infinite",
        cacheBlockSize: BLOCK_SIZE,
        maxConcurrentDatasourceRequests: -1,
        blockLoadDebounceMillis: 100,
        infiniteInitialRowCount: 10,
        rowHeight: 56,
        suppressNoRowsOverlay: true,
        suppressCellFocus: true,
        suppressMovableColumns: true,
        suppressMultiSort: true,
        sortingOrder: ["asc", "desc", null],
        unSortIcon: true,
        tooltipShowDelay: 300,
        getRowClass: (params) =>
          params.node && params.node.rowPinned === "top" ? "em-row-pinned" : "",
      },
      defaultColDef: {
        sortable: false,
        filter: false,
        resizable: false,
        suppressMenu: true,
      },
    };
  },
  computed: {
    ...mapState(["idtoken", "tokenUser", "empresa", "isOculto"]),
    isAdmin() {
      return !!(this.tokenUser && this.tokenUser.admin);
    },
    pinnedId() {
      return isValidCompanyId(this.empresa && this.empresa._id)
        ? this.empresa._id
        : null;
    },
    selectedName() {
      return (this.empresa && this.empresa.nombre) || "-";
    },
    modules() {
      return MODULES;
    },
    columnDefs() {
      return this.isAdmin ? ADMIN_COLUMNS : CLIENT_COLUMNS;
    },
    loadedCount() {
      return Object.keys(this.loadedBlocks).reduce(
        (sum, key) => sum + this.loadedBlocks[key],
        0,
      );
    },
    hasFilters() {
      return !!this.appliedSearch || !!this.moduleFilter;
    },
    hasAnyFilter() {
      return this.hasFilters || !!this.year;
    },
    filtersDescription() {
      return describeFilters(this.appliedSearch, this.moduleFilter, this.year);
    },
    moduleFilterLabel() {
      const mod = getModule(this.moduleFilter);
      return mod ? mod.label : "";
    },
    showGrid() {
      return (
        this.phase === "idle" ||
        this.phase === "loading" ||
        this.phase === "ready" ||
        (this.phase === "empty" && !!this.pinnedRow)
      );
    },
    isCompact() {
      return this.phase === "empty" && !!this.pinnedRow;
    },
    footerState() {
      return this.total !== null && this.loadedCount < this.total
        ? "Desliza para cargar más"
        : "";
    },
  },
  watch: {
    idtoken() {
      this.tryInit();
    },
    pinnedId(next, previous) {
      if (this.skipPinnedReload) {
        this.skipPinnedReload = false;
        return;
      }
      if (next && !previous && this.phase !== "idle") this.reload();
    },
  },
  beforeMount() {
    if (!this.isOculto) {
      this.$store.commit("ocultarSide");
    }
  },
  created() {
    this.skipPinnedReload = false;
    if (this.idtoken) this.tryInit();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    clearTimeout(this.editTimer);
    this.generation += 1;
    this.headerGeneration += 1;
  },
  methods: {
    formatNumber,
    cardTooltip(mod) {
      const detail = mod.tip ? ` (${mod.tip})` : "";
      const content =
        this.moduleFilter === mod.key
          ? `Quitar el filtro de ${mod.label}`
          : `Filtrar empresas con diagnósticos de ${mod.label}${detail}`;
      return { content, classes: "cultura-tooltip", placement: "top" };
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.tryInit();
    },
    tryInit() {
      if (!(this.gridApi && this.idtoken && this.phase === "idle")) return;
      this.reload();
      if (this.isAdmin) this.loadHeader({ reset: true });
    },
    reload(clearPinned) {
      if (!this.gridApi) return;
      this.generation += 1;
      this.loadedBlocks = {};
      this.total = null;
      this.blockError = null;
      this.phase = "loading";
      if (clearPinned === true) this.applyPinned(null);
      this.gridApi.setDatasource(this.buildDatasource());
      this.gridApi.ensureIndexVisible(0);
    },
    buildDatasource() {
      const generation = this.generation;
      const query = {
        year: this.year,
        search: this.appliedSearch,
        diagnostic: this.moduleFilter,
        pinned: this.hasAnyFilter ? null : this.pinnedId,
      };
      return {
        getRows: (params) => this.loadBlock(params, generation, query),
      };
    },
    async loadBlock(params, generation, query) {
      const limit = params.endRow - params.startRow;
      try {
        const block = await fetchCompaniesBlock(
          query.year,
          buildCompaniesParams({
            startRow: params.startRow,
            limit,
            search: query.search,
            diagnostic: query.diagnostic,
            pinned: query.pinned,
            sortModel: params.sortModel,
          }),
        );
        if (generation !== this.generation) {
          params.failCallback();
          return;
        }
        let rows = block.rows;
        let total = block.total;
        let pinned = query.pinned ? block.pinned : null;
        if (params.startRow === 0 && !this.isAdmin && pinned && total === 0) {
          rows = [pinned];
          total = 1;
          pinned = null;
        }
        const lastRow =
          rows.length < limit
            ? Math.min(params.startRow + rows.length, total)
            : total;
        this.$set(this.loadedBlocks, params.startRow, rows.length);
        this.total = total;
        this.blockError = null;
        if (params.startRow === 0) {
          this.applyPinned(pinned);
          this.phase = total === 0 ? "empty" : "ready";
        }
        params.successCallback(rows, lastRow);
      } catch (err) {
        if (generation !== this.generation) {
          params.failCallback();
          return;
        }
        this.handleBlockError(err, params, generation, query);
      }
    },
    handleBlockError(err, params, generation, query) {
      console.log(err);
      const status = err && err.response && err.response.status;
      if (status === 401 || status === 403) {
        this.phase = "forbidden";
        params.failCallback();
        return;
      }
      if (params.startRow === 0) {
        this.phase = "error";
        params.failCallback();
        return;
      }
      this.blockError = { params, generation, query };
    },
    retryBlock() {
      const pending = this.blockError;
      if (!pending) return;
      this.blockError = null;
      this.loadBlock(pending.params, pending.generation, pending.query);
    },
    applyPinned(row) {
      this.pinnedRow = row || null;
      if (this.gridApi) this.gridApi.setPinnedTopRowData(row ? [row] : []);
    },
    onSortChanged() {
      if (this.phase === "idle") return;
      this.reload();
    },
    async loadHeader(options) {
      if (!this.isAdmin) return;
      this.headerGeneration += 1;
      const generation = this.headerGeneration;
      const year = this.year;
      if (options.reset) this.header = EMPTY_HEADER;
      try {
        const header = await fetchCompaniesHeader(year);
        if (generation !== this.headerGeneration) return;
        this.header = header;
        if (header.companiesValidate) {
          this.validateList = header.companiesValidate;
        }
        if (!year && header.rangeDates) this.rangeDates = header.rangeDates;
      } catch (err) {
        if (generation !== this.headerGeneration) return;
        console.log(err);
        this.header = EMPTY_HEADER;
      }
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.applySearch, SEARCH_DEBOUNCE_MS);
    },
    applySearch() {
      const next = normalizeSearch(this.searchTerm);
      if (next === this.appliedSearch) return;
      this.appliedSearch = next;
      this.reload();
    },
    flushSearch() {
      clearTimeout(this.searchTimer);
      this.appliedSearch = normalizeSearch(this.searchTerm);
    },
    toggleModule(key) {
      this.flushSearch();
      this.moduleFilter = this.moduleFilter === key ? null : key;
      this.reload();
    },
    removeSearchChip() {
      clearTimeout(this.searchTimer);
      this.searchTerm = "";
      this.appliedSearch = "";
      this.reload();
    },
    removeModuleChip() {
      this.moduleFilter = null;
      this.reload();
    },
    clearFilters() {
      clearTimeout(this.searchTimer);
      this.searchTerm = "";
      this.appliedSearch = "";
      this.moduleFilter = null;
      this.reload();
    },
    onYearChange(year) {
      if (year === this.year) return;
      this.year = year;
      this.reload(true);
      this.loadHeader({ reset: true });
    },
    async selectCompany(row) {
      if (this.selectingIds.indexOf(row._id) > -1) return false;
      this.selectingIds.push(row._id);
      try {
        const empresa = await selectCompanyRequest(row._id);
        if (!this.pinnedId && isValidCompanyId(empresa && empresa._id)) {
          this.skipPinnedReload = true;
        }
        this.$store.commit("refreshEmpresa", empresa);
        return true;
      } catch (err) {
        console.log(err);
        await this.$swal({
          title: row.nombre,
          text: "Error al cambiar de empresa.",
          icon: "error",
          showConfirmButton: false,
          timer: NOTICE_MS,
        });
        return false;
      } finally {
        this.selectingIds = this.selectingIds.filter((id) => id !== row._id);
      }
    },
    async handleSelect(row) {
      const ok = await this.selectCompany(row);
      if (!ok) return;
      this.reload();
      await this.$swal({
        title: row.nombre,
        text: "Se seleccionó correctamente la empresa.",
        icon: "success",
        showConfirmButton: false,
        timer: NOTICE_MS,
      });
    },
    async handleDiagnosisClick(params) {
      if (!params.data || this.selectingIds.length) return;
      const row = params.data;
      const key = params.colDef.colId;
      const count = Number(row[key]) || 0;
      const path = resolveDiagnosisRoute(key, this.tokenUser);
      const ok = await this.selectCompany(row);
      if (!ok) return;
      if (path === null) {
        this.reload();
        await this.$swal({
          text: "Se seleccionó la empresa correctamente.",
          icon: "success",
          showConfirmButton: false,
          timer: NOTICE_MS,
        });
        return;
      }
      const last = count > 0 ? getLastProcess(row, key) : null;
      if (last) {
        await this.$swal({
          html: `Proceso <strong>${escapeHtml(
            last.nombre || last.name,
          )}</strong> seleccionado correctamente.`,
          icon: "success",
          showConfirmButton: false,
          timer: NOTICE_MS,
        });
      } else {
        await this.$swal({
          text: "Se selecciono la empresa correctamente. Redireccionando",
          icon: "success",
          showConfirmButton: false,
          timer: NOTICE_MS,
        });
      }
      this.$router.push(path);
    },
    openCreate() {
      clearTimeout(this.editTimer);
      this.sideMenuData.updateData = {};
      this.showSideMenu = true;
    },
    handleEdit(row) {
      clearTimeout(this.editTimer);
      if (this.showSideMenu) {
        this.sideMenuData.updateData = { ...row };
        return;
      }
      this.showSideMenu = true;
      this.editTimer = setTimeout(() => {
        this.sideMenuData.updateData = { ...row };
      }, EDIT_DELAY_MS);
    },
    closeSideMenu() {
      clearTimeout(this.editTimer);
      this.showSideMenu = false;
      this.sideMenuData.updateData = {};
    },
    onCompaniesChanged() {
      this.reload();
      this.loadHeader({ reset: false });
    },
    async handleDelete(row) {
      if (this.deletingIds.indexOf(row._id) > -1) return;
      const confirmation = await this.$swal({
        title: "¿Estás seguro?",
        text: "Al eliminar no se podrá recuperar la empresa después.",
        icon: "warning",
        showCancelButton: true,
        cancelButtonColor: "#de5b5b",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });
      if (!confirmation || !confirmation.isConfirmed) return;
      if (this.deletingIds.indexOf(row._id) > -1) return;
      this.deletingIds.push(row._id);
      let deleted = false;
      try {
        deleted = !!(await deleteCompanyRequest(row._id));
      } catch (err) {
        console.log(err);
      } finally {
        this.deletingIds = this.deletingIds.filter((id) => id !== row._id);
      }
      if (!deleted) {
        await this.$swal({
          title: row.nombre,
          text: "Error al eliminar la empresa.",
          icon: "error",
          showConfirmButton: false,
          timer: NOTICE_MS,
        });
        return;
      }
      if (this.total !== null) this.total = Math.max(this.total - 1, 0);
      if (this.gridApi) this.gridApi.refreshInfiniteCache();
      this.loadHeader({ reset: false });
      await this.$swal({
        title: row.nombre,
        text: "La empresa se elimino correctamente.",
        icon: "success",
        showConfirmButton: false,
        timer: NOTICE_MS,
      });
    },
  },
};
</script>

<style src="../styles.css"></style>

<style>
@import "~ag-grid-community/styles/ag-grid.css";
@import "~ag-grid-community/styles/ag-theme-material.css";
</style>



// WEBPACK FOOTER //
// src/components/empresas/views/Companies.vue